import React, { useState, useRef, useEffect } from 'react';
import TranscriptContainer from './TranscriptContainer';
import './TranscriptTabs.css';

const TranscriptTabs = ({ formattedTranscript, rawTranscript, interimTranscription, isRecording, isLive }) => {
  const [activeTab, setActiveTab] = useState('formatted');
  const formattedRef = useRef();
  const rawRef = useRef();

  useEffect(() => {
    // Check if scrollIntoView method exists before calling it (for test environment)
    if (formattedRef.current?.scrollIntoView) {
      formattedRef.current.scrollIntoView({ block: 'end' });
    }
    if (rawRef.current?.scrollIntoView) {
      rawRef.current.scrollIntoView({ block: 'end' });
    }
  }, [formattedTranscript, rawTranscript, interimTranscription]);

  // Function to format the transcript from JSON array to paragraphs
  const formatTranscriptParagraphs = (transcriptData) => {
    // If transcriptData is a string (old format), return as is
    if (typeof transcriptData === 'string') {
      return transcriptData;
    }
    
    // If transcriptData is an array (new format), check if it has the new structure
    if (Array.isArray(transcriptData)) {
      if (transcriptData.length === 0) return false; // Show empty state
      
      // Check if the first item has the new structure (speaker, displayDuration properties)
      const hasNewStructure = transcriptData[0] && 
        (transcriptData[0].hasOwnProperty('speaker') || 
         transcriptData[0].hasOwnProperty('displayDuration'));
      
      if (hasNewStructure) {
        // Use the new TranscriptContainer component for new structure
        return <TranscriptContainer paragraphs={transcriptData} showLoader={shouldShowLoader} interimTranscription={interimTranscription} />;
      } else {
        // Fallback to old format for backward compatibility
        return transcriptData.map((paragraph, index) => (
          <p key={index} className="transcript-paragraph">
            {paragraph.text}
          </p>
        ));
      }
    }
    
    // If transcriptData is an object with formattedTranscript property
    if (transcriptData && transcriptData.formattedTranscript && Array.isArray(transcriptData.formattedTranscript)) {
      if (transcriptData.formattedTranscript.length === 0) return false; // Show empty state
      
      // Check if the first item has the new structure
      const hasNewStructure = transcriptData.formattedTranscript[0] && 
        (transcriptData.formattedTranscript[0].hasOwnProperty('speaker') || 
         transcriptData.formattedTranscript[0].hasOwnProperty('displayDuration'));
      
      if (hasNewStructure) {
        // Use the new TranscriptContainer component for new structure
        return <TranscriptContainer paragraphs={transcriptData.formattedTranscript} showLoader={shouldShowLoader} interimTranscription={interimTranscription} />;
      } else {
        // Fallback to old format for backward compatibility
        return transcriptData.formattedTranscript.map((paragraph, index) => (
          <p key={index} className="transcript-paragraph">
            {paragraph.text}
          </p>
        ));
      }
    }
    
    // Fallback for unexpected data structure
    return false;
  };

  // Show loader when recording OR when live (for viewers)
  const shouldShowLoader = isRecording || isLive;

  return (
    <div className="transcript-tabs-container">
      <div className="tabs-header">
        <h3>Transcript</h3>
        <button
          className={`tab-button ${activeTab === 'formatted' ? 'active' : ''}`}
          onClick={() => setActiveTab('formatted')}
        >
          Formatted
        </button>
        <button
          className={`tab-button ${activeTab === 'raw' ? 'active' : ''}`}
          onClick={() => setActiveTab('raw')}
        >
          Raw
        </button>
      </div>

      <div className="tab-content">
        {activeTab === 'formatted' ? (
          <div className="transcript-panel">
            <div className="transcript-text">
              {formattedTranscript ? (
                  <div ref={formattedRef}>
                    {typeof formattedTranscript === 'string' ? (
                      <>
                        <pre dangerouslySetInnerHTML={{__html: formattedTranscript}}/>
                        {interimTranscription && (
                          <div className="interim-transcription">
                            <pre dangerouslySetInnerHTML={{__html: interimTranscription}}/>
                          </div>
                        )}
                        {shouldShowLoader && (
                          <div className="loader-container">
                            <div className="loader"></div>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="formatted-transcript">
                        {formatTranscriptParagraphs(formattedTranscript) || (
                          <div className="empty-transcript-state">
                            <div className="empty-state-icon">
                              <i className="ti ti-file-text-spark"></i>
                            </div>
                            <p className="empty-state-text">
                              Call transcript will automatically appear here as the meeting progresses.
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
              ) : rawTranscript ? (
                <div ref={formattedRef}>
                  <pre dangerouslySetInnerHTML={{__html: rawTranscript}}/>
                  {shouldShowLoader && (
                    <div className="loader-container">
                      <div className="loader"></div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="empty-transcript-state">
                  <div className="empty-state-icon">
                    <i className="ti ti-file-text-spark"></i>
                  </div>
                  <p className="empty-state-text">
                    Call transcript will automatically appear here as the meeting progresses.
                  </p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="transcript-panel">
            <div className="transcript-text">
              {rawTranscript ? (
                <div ref={rawRef}>
                  <pre dangerouslySetInnerHTML={{__html: rawTranscript}}/>
                  {shouldShowLoader && (
                    <div className="loader-container">
                      <div className="loader"></div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="no-transcript">No raw transcript available yet.</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TranscriptTabs; 
